<template>
  <div class="members-selection">
    <div class="members-selection-header">
      <div class="members-selection-header-search d-flex align-items-center justify-space-between">
        <AppInputSearch
          show-reset-button
          @reset="onSearchReset"
        >
          <AppInput
            ref="searchInput"
            v-model="searchString"
            type="search"
            :placeholder="searchPlaceholder"
            @keyup.enter="onSearchEnter"
          />
        </AppInputSearch>

        <div class="members-selection-header-options d-flex align-items-center mb-15 cursor-pointer">
          <a
            v-if="multiple && searchString.length === 0"
            :class="{ disabled: isSelectAllDisabled }"
            class="select-all-trigger"
            @click="handleSelectAllLink"
          >
            <strong>Select all</strong>
          </a>

          <strong
            class="clear-all-trigger"
            :class="{ disabled: isClearAllDisabled }"
            @click="handleClearAll"
          >
            Clear all
          </strong>
        </div>
      </div>
    </div>
    <template v-if="hasMembers">
      <div class="members-selection-list">
        <div class="members-selection-list-header d-flex align-items-center justify-space-between">
          <div class="d-flex align-items-center">
            <AppCheckbox
              v-if="multiple && searchString.length === 0"
              :value="isSelectAllActive"
              @input="handleSelectAllCheckbox"
            />

            <div>
              <strong class="text-capitalize">{{ recordsName }}</strong>
              ({{ counter }})
            </div>
          </div>

          <AppDropdown
            v-if="sortable"
            label="Sort by"
            direction="right"
            size="small"
            class="ml-0"
          >
            <AppDropdownItem
              v-for="option in sortOptions"
              :key="option.key"
              @click="handleSort(option)"
            >
              {{ option.label }}
            </AppDropdownItem>
          </AppDropdown>
        </div>

        <div class="members-selection-list-table">
          <div
            v-for="member in members"
            :key="member.id"
            :highlight="firstItem?.id === member.id && !!searchString"
            class="members-selection-list-table-row d-flex align-items-center"
          >
            <AppCheckbox
              :value="member.selected"
              @input="toggleSelection(member)"
            >
              {{ member.name }}
              <span v-if="member.status === 'Archived'">(Archived)</span>
            </AppCheckbox>
          </div>
        </div>

        <AppPagination
          class="py-15 px-20"
          :pagination="pagination"
          :resource="resource"
          :loading="loading"
          hide-pagination-showing-text
          @page-changed="changePage"
        />
      </div>
    </template>

    <div
      v-else
      class="no-results my-20"
    >
      {{ noMembersMessage }}
    </div>
  </div>
</template>

<script>
import { MEMBERS_SELECTION_SORT_OPTIONS } from '~/app_constants';

export default {
  name: 'AppMembersSelection',
  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    fetching: {
      type: Boolean,
      required: true,
    },
    visible: {
      type: Boolean,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    members: {
      type: Array,
      default: () => [],
    },
    changedMembers: {
      type: Array,
      default: () => [],
    },
    pagination: {
      type: Object,
      required: true,
    },
    selectAllButtonEnabled: {
      type: [Boolean, null],
      default: null,
    },
    selectionsCount: {
      type: Number,
      default: 0,
    },
    searchPlaceholder: {
      type: String,
      required: true,
    },
    query: {
      type: String,
      default: '',
    },
    recordsName: {
      type: String,
      default: 'members',
    },
  },
  data: () => ({
    sortBy: '',
    page: 1,
  }),
  computed: {
    counter() {
      return `${this.selectionsCount}/${this.pagination.total_count}`;
    },
    hasMembers() {
      return !!this.members.length;
    },
    noMembersMessage() {
      return this.fetching ? 'Loading data...' : `No ${this.recordsName} found.`;
    },
    searchString: {
      get() {
        return this.query;
      },
      set(query) {
        if (!this.loading) {
          this.$emit('search', query);
        }
      },
    },
    canClearAll() {
      return (
        this.members.some((m) => m.selected) ||
        this.changedMembers.some((m) => m.selected) ||
        !!this.isSelectAllActive ||
        this.pagination.selected_count > 0
      );
    },
    isSelectAllActive() {
      return !!this.selectAllButtonEnabled || this.selectionsCount === this.pagination.total_count;
    },
    isSelectAllDisabled() {
      return this.isSelectAllActive || !this.hasMembers;
    },
    isClearAllDisabled() {
      return !this.canClearAll || !this.hasMembers;
    },
    sortOptions() {
      return MEMBERS_SELECTION_SORT_OPTIONS;
    },
    resource() {
      return { one: this.recordsName.substring(0, this.recordsName.length - 1), many: this.recordsName };
    },
    firstItem() {
      const [firstItem] = this.members;

      return firstItem;
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.focusSearch();
        }
      },
    },
  },
  methods: {
    toggleSelection(member) {
      this.$emit('toggle-selection', member);
    },
    handleSelectAllCheckbox() {
      this.$emit(this.isSelectAllActive ? 'clear-all' : 'select-all');
    },
    handleSelectAllLink(e) {
      e.preventDefault();

      if (!this.loading && !this.isSelectAllDisabled) {
        this.$emit('select-all');
      }
    },
    handleClearAll(e) {
      e.preventDefault();

      if (!this.loading && !this.isClearAllDisabled) {
        this.$emit('clear-all');
      }
    },
    changePage(page = 1) {
      if (!this.loading) {
        this.page = page;
        this.$emit('update', { page, sort: this.sortBy });
      }
    },
    handleSort({ field, value }) {
      if (this.sortable) {
        this.sortBy = `${field},${value}`;
        this.$emit('sort', this.page, this.sortBy);
      }
    },
    getSearchRef() {
      const searchInput = this.$refs.searchInput;

      return searchInput?.$el;
    },
    focusSearch() {
      this.getSearchRef()?.focus();
    },
    onSearchEnter() {
      if (this.searchString) {
        if (this.firstItem) {
          if (!this.fetching) {
            this.toggleSelection(this.firstItem);
          }
          // Select the input for a better UX. This way the user can quickly remove the text
          this.focusSearch();
          this.getSearchRef().select();
        }
      }
    },
    onSearchReset() {
      this.searchString = '';
    },
  },
};
</script>
